import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../../utils/Customer/apiBaseQuery";
import {
  CustomerAnalyticsResponse,
  IAddSubcategoryResponse,
  IBatchErrorResponse,
  IBatchHistoryResponse,
  IBatchStatusResponse,
  IBatchSummaryResponse,
  ICategory,
  IDashboardDataResponse,
  IInventoryUploadResponse,
  ILowStockProductResponse,
  IOrderStatusAnalyticsResponse,
  IPauseAndResumeSubscriptionResponse,
  ISubscriptionDetails,
  ISubscriptionListResponse,
  ISubscriptionStatsResponse,
  ISystemDefinedCategories,
} from "../../../types/Response/AdminResponse";
import {
  ADD_SUBCATEGORY,
  ADMIN_CATEGORIES,
  ADMIN_SUBSCRIPTION,
  ADMIN_SUBSCRIPTION_DASHBOARD,
  BATCH_ERRORS,
  BATCH_PUBLISH,
  BATCH_STATUS,
  BATCH_SUMMARY,
  BATCH_VALIDATE,
  DASHBOARD_ADMIN_ANALYTICS,
  DASHBOARD_CUSTOMER_ANALYTICS,
  DASHBOARD_DATA,
  DASHBOARD_LOW_STOCK_PRODUCTS,
  DASHBOARD_ORDER_STATUS_ANALYTICS,
  DOWNLOAD_CATEGORIES,
  GET_CATEGORIES,
  INVENTORY_EXPORT,
  SYSTEM_DEFINED_CATEGORIES,
  UPLOAD_INVENTORY,
} from "../../../config/apiUrlsAdmin";
import {
  IAddSubcategoryRequest,
  IAnalyticsDashboardResponse,
  IBatchHistoryRequest,
  IChangeBrandStatusRequest,
  IDashboardFilterRequest,
  IInventoryUploadRequest,
  ISubscriptionFilterParams,
  IUpdateCategoryRequest,
  IUpdateSubBillingCycleAdminRequest,
} from "../../../types/Request/AdminRequest";
import {
  IUpdateSubBillingCycleRequest,
  IUpdateSubQuantityRequest,
} from "../../../types/Request/CustomerRequest";

export const adminDashboardAPi = createApi({
  reducerPath: "adminDashboardAPi",
  baseQuery: axiosBaseQuery(),

  endpoints: (builder) => ({
    dashboardData: builder.query<
      IDashboardDataResponse,
      IDashboardFilterRequest
    >({
      query: (params) => ({
        url: `${DASHBOARD_DATA}`,
        method: "get",
        params,
      }),
    }),

    dashboardLowStock: builder.query<
      ILowStockProductResponse,
      IDashboardFilterRequest
    >({
      query: (params) => ({
        url: `${DASHBOARD_LOW_STOCK_PRODUCTS}`,
        method: "get",
        params,
      }),
    }),

    dashboardOrderStatusAnalytics: builder.query<
      IOrderStatusAnalyticsResponse,
      IDashboardFilterRequest
    >({
      query: (params) => ({
        url: `${DASHBOARD_ORDER_STATUS_ANALYTICS}`,
        method: "get",
        params,
      }),
    }),
    dashboardCustomerAnalytics: builder.query<
      CustomerAnalyticsResponse,
      IDashboardFilterRequest
    >({
      query: (params) => ({
        url: `${DASHBOARD_CUSTOMER_ANALYTICS}`,
        method: "get",
        params,
      }),
    }),

    analyticsDashboard: builder.query<
      IAnalyticsDashboardResponse,
      IDashboardFilterRequest
    >({
      query: (params) => ({
        url: `${DASHBOARD_ADMIN_ANALYTICS}`,
        method: "get",
        params,
      }),
    }),
    getCategory: builder.query<ICategory[], void>({
      query: () => ({
        url: `${GET_CATEGORIES}`,
        method: "get",
      }),
    }),
    getSystemDefinedCategory: builder.query<ISystemDefinedCategories[], void>({
      query: () => ({
        url: `${SYSTEM_DEFINED_CATEGORIES}`,
        method: "get",
      }),
    }),
    getCategoryByParentId: builder.query<ISystemDefinedCategories[], number>({
      query: (ParentCategoryId) => ({
        url: `${ADMIN_CATEGORIES}/${ParentCategoryId}/children`,
        method: "get",
      }),
    }),
    downloadCategories: builder.query<Blob, void>({
      query: () => ({
        url: DOWNLOAD_CATEGORIES,
        method: "get",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    addSubcategory: builder.mutation<
      IAddSubcategoryResponse,
      IAddSubcategoryRequest
    >({
      query: (data) => ({
        url: `${ADD_SUBCATEGORY}`,
        method: "post",
        data: data,
      }),
    }),

    updateCategories: builder.mutation<
      IAddSubcategoryResponse,
      { id: number; data: IUpdateCategoryRequest }
    >({
      query: ({ id, data }) => ({
        url: `${ADMIN_CATEGORIES}/${id}/update`,
        method: "put",
        data: data,
      }),
    }),
    ChangeCategoriesStatus: builder.mutation<
      IAddSubcategoryResponse,
      { id: number; isActive: IChangeBrandStatusRequest }
    >({
      query: ({ id, isActive }) => ({
        url: `${ADMIN_CATEGORIES}/${id}/change-status`,
        method: "patch",
        data: isActive,
      }),
    }),

    inventoryUpload: builder.mutation<
      IInventoryUploadResponse,
      IInventoryUploadRequest
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: UPLOAD_INVENTORY,
          method: "post",
          data: formData,
        };
      },
    }),
    batchSummary: builder.query<IBatchSummaryResponse, string>({
      query: (batchId) => ({
        url: `${BATCH_SUMMARY}/${batchId}`,
        method: "get",
      }),
    }),
    batchStatus: builder.query<IBatchStatusResponse, string>({
      query: (batchId) => ({
        url: `${BATCH_STATUS}/${batchId}`,
        method: "get",
      }),
    }),
    batchErrors: builder.query<IBatchErrorResponse, string>({
      query: (batchId) => ({
        url: `${BATCH_ERRORS}/${batchId}/errors`,
        method: "get",
      }),
    }),
    InventoryExport: builder.query<Blob, void>({
      query: () => ({
        url: INVENTORY_EXPORT,
        method: "get",
        responseHandler: async (response: Response) => response.blob(),
      }),
      transformResponse: (response: Blob) => response,
    }),
    batchHistory: builder.query<IBatchHistoryResponse, IBatchHistoryRequest>({
      query: ({
        batchId,
        PageNumber,
        PageSize,
        Status,
        ProcessingStatus,
        FromDate,
        ToDate,
      }) => ({
        url: BATCH_ERRORS,
        method: "get",
        params: {
          PageNumber,
          PageSize,
          Status,
          ProcessingStatus,
          FromDate,
          ToDate,
        },
      }),
    }),
    batchPublish: builder.mutation<string, string>({
      query: (batchId) => ({
        url: `${BATCH_PUBLISH}/${batchId}`,
        method: "post",
      }),
    }),
    batchValidate: builder.mutation<string, string>({
      query: (batchId) => ({
        url: `${BATCH_VALIDATE}/${batchId}`,
        method: "post",
      }),
    }),

    AdminSubscriptionDashboard: builder.query<ISubscriptionStatsResponse, void>(
      {
        query: () => ({
          url: ADMIN_SUBSCRIPTION_DASHBOARD,
          method: "get",
        }),
      },
    ),
    AdminSubscriptions: builder.query<
      ISubscriptionListResponse,
      ISubscriptionFilterParams
    >({
      query: (data) => ({
        url: ADMIN_SUBSCRIPTION,
        method: "get",
        data: data,
      }),
    }),
    AdminSubscriptionsById: builder.query<ISubscriptionDetails, number>({
      query: (subId) => ({
        url: `${ADMIN_SUBSCRIPTION}/${subId}`,
        method: "get",
      }),
    }),

    pauseSubscription: builder.mutation<
      IPauseAndResumeSubscriptionResponse,
      number
    >({
      query: (id) => ({
        url: `${ADMIN_SUBSCRIPTION}/${id}/pause`,
        method: "put",
      }),
    }),

    resumeSubscription: builder.mutation<
      IPauseAndResumeSubscriptionResponse,
      number
    >({
      query: (id) => ({
        url: `${ADMIN_SUBSCRIPTION}/${id}/resume`,
        method: "put",
      }),
    }),

    adminSubscriptionRenew: builder.mutation<
      IPauseAndResumeSubscriptionResponse,
      number
    >({
      query: (id) => ({
        url: `${ADMIN_SUBSCRIPTION}/${id}/renew-now`,
        method: "post",
      }),
    }),
    updateSubscriptionQuantityAdmin: builder.mutation<
      IPauseAndResumeSubscriptionResponse,
      { id: number; data: IUpdateSubQuantityRequest }
    >({
      query: ({ id, data }) => ({
        url: `${ADMIN_SUBSCRIPTION}/${id}/quantity`,
        method: "put",
        data: data,
      }),
    }),
    cancelSubscription: builder.mutation<
      IPauseAndResumeSubscriptionResponse,
      number
    >({
      query: (id) => ({
        url: `${ADMIN_SUBSCRIPTION}/${id}/cancel`,
        method: "put",
      }),
    }),

    ChangeBlilingCycleAdmin: builder.mutation<
      IPauseAndResumeSubscriptionResponse,
      { id: number; data: IUpdateSubBillingCycleAdminRequest }
    >({
      query: ({ id, data }) => ({
        url: `${ADMIN_SUBSCRIPTION}/${id}/billing`,
        method: "put",
        data: data,
      }),
    }),
  }),
});

export const {
  useDashboardDataQuery,

  // Dashboard analytics
  useDashboardCustomerAnalyticsQuery,
  useDashboardLowStockQuery,
  useDashboardOrderStatusAnalyticsQuery,
  useLazyDashboardCustomerAnalyticsQuery,
  useLazyDashboardLowStockQuery,
  useLazyDashboardOrderStatusAnalyticsQuery,
  useAnalyticsDashboardQuery,
  useLazyGetCategoryQuery,
  useLazyDownloadCategoriesQuery,
  useAddSubcategoryMutation,
  useInventoryUploadMutation,
  useLazyBatchStatusQuery,
  useLazyBatchSummaryQuery,
  useLazyBatchErrorsQuery,
  useBatchPublishMutation,
  useBatchValidateMutation,
  useLazyBatchHistoryQuery,
  useLazyInventoryExportQuery,
  useLazyGetCategoryByParentIdQuery,
  useLazyGetSystemDefinedCategoryQuery,
  useUpdateCategoriesMutation,
  useChangeCategoriesStatusMutation,

  useLazyAdminSubscriptionDashboardQuery,
  useLazyAdminSubscriptionsByIdQuery,
  useLazyAdminSubscriptionsQuery,
  usePauseSubscriptionMutation,
  useResumeSubscriptionMutation,
  useAdminSubscriptionRenewMutation,
  useCancelSubscriptionMutation,
  useUpdateSubscriptionQuantityAdminMutation,
  useChangeBlilingCycleAdminMutation,
} = adminDashboardAPi;
